import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ImageOff, ShieldX } from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { SectionHeading } from "@/components/section-heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { decideImageReview, listImageReviews } from "@/lib/admin.functions";

export const Route = createFileRoute("/_authenticated/admin/moderation/images")({
  head: () => ({
    meta: [
      { title: "Image moderation — Quibblies" },
      {
        name: "description",
        content:
          "Administrator queue for reviewing Quibblies profile and cover images before they appear publicly.",
      },
      { property: "og:title", content: "Image moderation — Quibblies" },
      { property: "og:description", content: "Approve or reject member profile and cover images." },
      { property: "og:type", content: "website" },
      { name: "robots", content: "noindex" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ImageModerationPage,
});

function ImageModerationPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(listImageReviews);
  const decide = useServerFn(decideImageReview);
  const [status, setStatus] = React.useState<"pending" | "approved" | "rejected">("pending");
  const [reasons, setReasons] = React.useState<Record<string, string>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-image-reviews", status],
    queryFn: () => load({ data: { status } }),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: (input: { id: string; decision: "approved" | "rejected"; reason: string }) =>
      decide({ data: input }),
    onSuccess: (_r, input) => {
      toast.success(input.decision === "approved" ? "Image published" : "Image rejected");
      void queryClient.invalidateQueries({ queryKey: ["admin-image-reviews"] });
    },
    onError: (e: Error) => toast.error("Could not save the decision", { description: e.message }),
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Profile image moderation"
        description="Profile and cover images stay private until an Administrator approves them. Rejected uploads are deleted from storage."
      />

      <Tabs
        value={status}
        onValueChange={(v) => setStatus(v as typeof status)}
        className="mt-6"
      >
        <TabsList>
          <TabsTrigger value="pending">Awaiting review</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <Skeleton className="mt-8 h-64 w-full rounded-2xl" />
      ) : error ? (
        <div className="mt-8">
          <EmptyState
            icon={ShieldX}
            title="Administrator access required"
            description="Image moderation is restricted to Administrators and the Owner."
          />
        </div>
      ) : !data?.length ? (
        <div className="mt-8">
          <EmptyState
            icon={ImageOff}
            title="Nothing here"
            description="No images in this state right now."
          />
        </div>
      ) : (
        <ul className="mt-8 grid gap-4 sm:grid-cols-2">
          {data.map((image) => (
            <li key={image.id} className="rounded-2xl border border-border/60 bg-surface/50 p-4">
              <div className="overflow-hidden rounded-xl border border-border/60 bg-background/40">
                {image.url ? (
                  <img
                    src={image.url}
                    alt={`${image.kind === "avatar" ? "Profile" : "Cover"} image submitted by @${image.username}`}
                    className={
                      image.kind === "avatar"
                        ? "mx-auto h-40 w-40 object-cover"
                        : "h-40 w-full object-cover"
                    }
                    loading="lazy"
                  />
                ) : (
                  <p className="p-6 text-center text-xs text-muted-foreground">
                    Image unavailable
                  </p>
                )}
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="text-sm text-foreground">@{image.username}</p>
                <Badge variant="outline">
                  {image.kind === "avatar" ? "Profile picture" : "Cover image"}
                </Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Uploaded {new Date(image.createdAt).toLocaleString()}
              </p>
              {image.reason ? (
                <p className="mt-2 text-xs text-muted-foreground">Note: {image.reason}</p>
              ) : null}

              {status === "pending" ? (
                <>
                  <Textarea
                    className="mt-3"
                    rows={2}
                    placeholder="Reason sent to the member (required to reject)"
                    value={reasons[image.id] ?? ""}
                    onChange={(e) => setReasons((r) => ({ ...r, [image.id]: e.target.value }))}
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="hero"
                      disabled={mutation.isPending}
                      onClick={() =>
                        mutation.mutate({
                          id: image.id,
                          decision: "approved",
                          reason: reasons[image.id] ?? "",
                        })
                      }
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={mutation.isPending}
                      onClick={() => {
                        const reason = reasons[image.id]?.trim() ?? "";
                        if (!reason) {
                          toast.error("Add a reason", {
                            description: "Members are told why an image was rejected.",
                          });
                          return;
                        }
                        mutation.mutate({ id: image.id, decision: "rejected", reason });
                      }}
                    >
                      Reject
                    </Button>
                  </div>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
